import { useEffect, useState } from 'react';
import type { ArchiveRecord } from '../types/moderation';
import { ApiError, moderateSubmission } from '../lib/api';
import { announce } from '../lib/announce';
import { toast } from '../lib/toast';
import {
  REJECT_REASON_TEMPLATES,
  REQUEST_CHANGES_TEMPLATES,
  type ReasonTemplate,
} from '../lib/reason-templates';
import BottomSheet from './BottomSheet';

/**
 * The verbs a moderator can pick in the mobile composer. Excludes
 * `pending_review` (only used as a record status) and
 * `awaiting_peer_review` (set automatically by the server when a vote
 * needs a second moderator, never picked from the UI).
 */
export type MobileComposerAction =
  | 'approved'
  | 'rejected'
  | 'withdrawn'
  | 'awaiting_contributor';

interface MobileActionComposerProps {
  open: boolean;
  /** Which moderation verb the moderator picked. */
  action: MobileComposerAction | null;
  record: ArchiveRecord;
  onOpenChange: (open: boolean) => void;
  /** Called after the API confirms the moderation; triggers a record refresh. */
  onModerated: () => void;
  disabled?: boolean;
}

const ACTION_TITLES: Record<MobileComposerAction, string> = {
  approved: 'Approve submission',
  rejected: 'Reject submission',
  withdrawn: 'Withdraw submission',
  awaiting_contributor: 'Request changes',
};

const ACTION_DESCRIPTIONS: Record<MobileComposerAction, string> = {
  approved: 'Publish this to the public archive. You can add an internal note.',
  rejected:
    'The contributor will be emailed your reason. Pick a starter or write your own.',
  withdrawn: 'Remove this from the public archive. Note explains why.',
  awaiting_contributor:
    'Pause moderation and ask the contributor for more information.',
};

const ACTION_PAST_TENSE: Record<MobileComposerAction, string> = {
  approved: 'approved',
  rejected: 'rejected',
  withdrawn: 'withdrawn',
  awaiting_contributor: 'sent to the contributor for changes',
};

const ACTION_CONFIRM_LABEL: Record<MobileComposerAction, string> = {
  approved: 'Confirm approve',
  rejected: 'Confirm reject',
  withdrawn: 'Confirm withdraw',
  awaiting_contributor: 'Send to contributor',
};

function templatesFor(action: MobileComposerAction | null): ReasonTemplate[] {
  if (action === 'rejected') return REJECT_REASON_TEMPLATES;
  if (action === 'awaiting_contributor') return REQUEST_CHANGES_TEMPLATES;
  return [];
}

/**
 * Mobile bottom-sheet composer for moderation decisions.
 *
 * Replaces the desktop Radix Dialog when on a phone. Renders the reject
 * reason templates as wrap-friendly chips above a textarea sized for a
 * thumb, with an Esc / scrim / drag-down dismiss and a sticky footer
 * that sits above the iOS safe-area inset.
 *
 * Behaviour matches the desktop ModerationActions component:
 * - Approve / withdraw note is optional.
 * - Reject expects a reason but does not require one (matches desktop).
 * - Request-changes requires a question so the contributor has something
 *   to respond to.
 * - Stale-edit (409) surfaces a "Refresh (loses your note)" affordance.
 */
export default function MobileActionComposer({
  open,
  action,
  record,
  onOpenChange,
  onModerated,
  disabled = false,
}: MobileActionComposerProps) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [staleEdit, setStaleEdit] = useState(false);
  const [pickedTemplate, setPickedTemplate] = useState<string | null>(null);

  // Reset everything whenever the composer opens / the action changes.
  useEffect(() => {
    if (open && action) {
      setNote('');
      setError(null);
      setStaleEdit(false);
      setPickedTemplate(null);
    }
  }, [open, action]);

  const templates = templatesFor(action);

  const handleConfirm = async () => {
    if (!action || disabled) return;
    setBusy(true);
    setError(null);
    setStaleEdit(false);
    try {
      await moderateSubmission(
        record.submissionId,
        action,
        note.trim() || undefined,
        record.updatedAt,
      );
      toast.success(
        `${record.title || 'Submission'} ${ACTION_PAST_TENSE[action] || action}.`,
        { announce: false },
      );
      announce(
        `Submission ${record.title} ${ACTION_PAST_TENSE[action] || action}.`,
      );
      onModerated();
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setStaleEdit(true);
        setError(
          'Someone else updated this submission. Refresh to see the latest before acting.',
        );
      } else {
        setError((err as Error).message || 'Moderation failed');
      }
    } finally {
      setBusy(false);
    }
  };

  const requireNote = action === 'awaiting_contributor';
  const noteEmpty = !note.trim();

  if (!action) {
    return (
      <BottomSheet
        open={open}
        onOpenChange={onOpenChange}
        title=""
        testId="action-composer"
      >
        <p>No action selected.</p>
      </BottomSheet>
    );
  }

  const footer = (
    <div className="action-composer__footer-row">
      {staleEdit && (
        <button
          type="button"
          className="btn btn-secondary action-composer__refresh"
          onClick={() => {
            onModerated();
            onOpenChange(false);
          }}
          data-testid="composer-refresh"
        >
          Refresh
        </button>
      )}
      <button
        type="button"
        className="btn btn-secondary action-composer__cancel"
        onClick={() => onOpenChange(false)}
        disabled={busy}
        data-testid="composer-cancel"
      >
        Cancel
      </button>
      <button
        type="button"
        className={`btn btn-primary action-composer__confirm action-composer__confirm--${action}`}
        onClick={handleConfirm}
        disabled={busy || disabled || (requireNote && noteEmpty)}
        data-testid="composer-confirm"
      >
        {busy ? 'Saving…' : ACTION_CONFIRM_LABEL[action]}
      </button>
    </div>
  );

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title={ACTION_TITLES[action]}
      description={ACTION_DESCRIPTIONS[action]}
      footer={footer}
      className={`action-composer action-composer--${action}`}
      testId="action-composer"
    >
      {templates.length > 0 && (
        <div className="action-composer__templates" data-testid="composer-templates">
          <span className="action-composer__templates-label">
            Quick starters
          </span>
          <div className="action-composer__chip-row">
            {templates.map((tpl) => (
              <button
                key={tpl.key}
                type="button"
                className={`action-composer__chip${
                  pickedTemplate === tpl.key
                    ? ' action-composer__chip--active'
                    : ''
                }`}
                onClick={() => {
                  setNote(tpl.body);
                  setPickedTemplate(tpl.key);
                }}
                data-testid={`composer-template-${tpl.key}`}
                aria-pressed={pickedTemplate === tpl.key}
              >
                {tpl.label}
              </button>
            ))}
          </div>
        </div>
      )}
      <label className="action-composer__field">
        <span className="action-composer__label">
          {action === 'awaiting_contributor'
            ? 'Question for the contributor'
            : action === 'rejected'
            ? 'Reason (sent to the contributor)'
            : 'Note (optional)'}
          {requireNote && (
            <span className="action-composer__required" aria-hidden="true">
              *
            </span>
          )}
        </span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={
            action === 'rejected' || action === 'awaiting_contributor' ? 5 : 3
          }
          className="action-composer__textarea"
          placeholder={
            action === 'awaiting_contributor'
              ? 'What do you need from the contributor?'
              : action === 'rejected'
              ? 'Tell the contributor why. They can respond.'
              : 'Add context for the audit trail.'
          }
          inputMode="text"
          autoComplete="off"
          enterKeyHint="done"
          data-testid="composer-note"
        />
      </label>
      {error && (
        <div className="field-error" role="alert" data-testid="composer-error">
          {error}
        </div>
      )}
    </BottomSheet>
  );
}
